import { CredentialWithVerification } from 'types';
import { buildCredentialString } from './utils';
import moment from 'moment';
import { ATSCredentialsWithVerificationTestIds } from 'data-testids/ATS';
import { Box, Typography, useTheme } from '@mui/material';
import { CredentialContentContainer, CredentialSummary } from './styles';
import { VerifiedUser } from '@mui/icons-material';
import { SecondaryButton } from 'sharedComponents/Buttons';
import { CredentialDetailModal } from './CredentialDetailModal';
import { useState } from 'react';
import { CredentialVerificationStatus } from 'utils/typedEnums';

export const VerifiedCredentialListItem: React.FC<{ credential: CredentialWithVerification }> = ({
  credential,
}) => {
  const theme = useTheme();
  const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);

  const credentialHasScreenshot = Boolean(credential.screenshot_url);
  const showManuallyVerifiedText =
    credential.verification_status === CredentialVerificationStatus.PASSED_MANUAL_VERIFICATION &&
    !credentialHasScreenshot;

  const manuallyVerifiedText = 'Manually verified through Nimble Connect';

  const onClickViewCredential = () => {
    setDetailModalIsOpen(true);
  };

  return (
    <>
      <Box
        data-testid={ATSCredentialsWithVerificationTestIds.CREDENTIAL_LIST_ITEM}
        sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}
      >
        <VerifiedCredential credential={credential} />

        <Box
          sx={{ marginLeft: theme.spacing(3) }}
          data-testid={ATSCredentialsWithVerificationTestIds.VERIFIED_CREDENTIAL_DETAIL_CONTAINER}
        >
          {credentialHasScreenshot && (
            <SecondaryButton
              dataTestId={ATSCredentialsWithVerificationTestIds.VIEW_CREDENTIAL_BUTTON}
              onClick={onClickViewCredential}
            >
              View Credential
            </SecondaryButton>
          )}
          {showManuallyVerifiedText && (
            <Typography
              data-testid={ATSCredentialsWithVerificationTestIds.MANUALLY_VERIFIED_TEXT}
              sx={{ fontStyle: 'italic', fontSize: theme.typography.body3.fontSize }}
            >
              {manuallyVerifiedText}
            </Typography>
          )}
        </Box>
      </Box>
      <CredentialDetailModal
        open={detailModalIsOpen}
        handleClose={() => setDetailModalIsOpen(false)}
        credential={credential}
      />
    </>
  );
};

export const VerifiedCredential: React.FC<{ credential: CredentialWithVerification }> = ({
  credential,
}) => {
  const theme = useTheme();
  const credentialString = buildCredentialString(credential);

  const getVerifiedCopy = () => {
    const stateToDepartmentName = {
      MO: 'Missouri Department of Elementary and Secondary Education',
      TN: 'Tennessee Department of Education',
    };

    const verifiedFrom = stateToDepartmentName[credential.state];
    const verifiedOn = moment(credential.verified_on).format('MM/DD/YYYY [at] hh:mm a');

    if (!verifiedFrom) {
      return `Verified on ${verifiedOn}`;
    }

    return `Verified from ${verifiedFrom} on ${verifiedOn}`;
  };

  return (
    <CredentialContentContainer
      data-testid={ATSCredentialsWithVerificationTestIds.VERIFIED_CREDENTIAL}
    >
      <VerifiedUser
        data-testid={ATSCredentialsWithVerificationTestIds.VERIFIED_ICON}
        sx={{ color: theme.palette.primary.main, height: 20, width: 20 }}
      />
      <Box>
        <CredentialSummary
          data-testid={ATSCredentialsWithVerificationTestIds.CREDENTIAL_EXPLANATION}
        >
          {credentialString}
        </CredentialSummary>
        <Typography
          data-testid={ATSCredentialsWithVerificationTestIds.VERIFICATION_SUBTEXT}
          sx={{ fontSize: theme.typography.body3.fontSize }}
        >
          {getVerifiedCopy()}
        </Typography>
      </Box>
    </CredentialContentContainer>
  );
};
